import axios from "axios";
import { getTokenHeader } from "@/helpers/commonHelper";

const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);

export default {
  name: "FormSubmissions",
  data() {
    return {
      submissions: [],
      formStructures: {},
      formPropertyCache: {},
      loading: false,
      exportLoading: false,
      missingFormSelectionDialog: false,
      exportDialogMessage: "",
      resendLoadingId: null,
      selectAllLoading: false,
      batchDeleteLoading: false,
      filters: {
        search: "",
        formId: "",
        status: ""
      },
      viewDialog: false,
      editStatusDialog: false,
      warningDialog: false,
      warningAction: null,
      warningSubmission: null,
      warningSubmissions: [],
      selectedSubmission: null,
      editingSubmissionStatus: "",
      editingSubmissionNotes: "",
      selected: [],
      allPagesSelected: false,
      allPagesSelectionCount: 0,
      pagination: {
        page: 1,
        itemsPerPage: 10,
        total: 0
      },
      headers: [
        { text: "Formular", value: "formName", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "Eingereicht am", value: "createdAt", sortable: false },
        { text: "Aktionen", value: "actions", sortable: false, align: "center" }
      ]
    };
  },

  async created() {
    await this.$store.dispatch("getCurrentUser");
    await this.$store.dispatch("getGroups");

    // Berechtigung prüfen
    const currentUser = this.$store.state.currentUser;
    const hasSubmissionReadAccess =
      currentUser?.scopes?.includes("role:submission_read") ||
      currentUser?.scopes?.includes("role:admin");
    if (!hasSubmissionReadAccess) {
      this.$router.push("/forms");
      return;
    }

    await this.fetchSubmissions();
  },

  methods: {
    async exportFilteredSubmissionsCsv() {
      if (this.exportLoading) return;

      this.exportLoading = true;
      try {
        const selectedSubmissions = (this.selected || []).filter(
          item => !!item?._id
        );
        let exportSubmissions = [];
        let exportFormId = "";

        if (selectedSubmissions.length) {
          const uniqueFormIds = [
            ...new Set(
              selectedSubmissions.map(item => this.getFormId(item.form_id))
            )
          ].filter(Boolean);

          if (uniqueFormIds.length !== 1) {
            this.exportDialogMessage =
              "Bitte in der Auswahl nur Einreichungen eines Formulars markieren.";
            this.missingFormSelectionDialog = true;
            return;
          }

          exportSubmissions = selectedSubmissions;
          exportFormId = uniqueFormIds[0];
        } else {
          if (!this.filters.formId) {
            this.exportDialogMessage =
              "Bitte zuerst ein Formular im Filter auswählen, damit der CSV-Export mit einheitlichen Feldern erstellt werden kann.";
            this.missingFormSelectionDialog = true;
            return;
          }

          const allSubmissions = await this.fetchAllSubmissions();
          exportSubmissions = this.applySubmissionFilters(allSubmissions);
          exportFormId = this.filters.formId;
        }

        if (!exportSubmissions.length) {
          this.$root.$snackbar(
            "Keine Einreichungen fuer den Export gefunden",
            "error"
          );
          return;
        }

        await this.loadFormStructure(exportFormId);
        const orderedFieldIds = this.getOrderedFieldIds(
          exportFormId,
          exportSubmissions
        );
        const csv = this.buildSubmissionCsv(
          exportSubmissions,
          orderedFieldIds,
          exportFormId
        );
        const fileName = this.buildSubmissionCsvFilename(exportFormId);
        this.downloadCsvFile(csv, fileName);
      } catch (error) {
        console.error("Fehler beim CSV-Export der Einreichungen:", error);
        this.$root.$snackbar("Fehler beim CSV-Export", "error");
      } finally {
        this.exportLoading = false;
      }
    },
    getOrderedFieldIds(formId, submissions = []) {
      const structureFields = this.formStructures[formId]?.data?.fields || [];
      const orderedFromStructure = this.collectFieldIdsFromStructure(
        structureFields
      );
      const seen = new Set(orderedFromStructure);

      (submissions || []).forEach(submission => {
        const keys = Object.keys(submission?.data || {});
        keys.forEach(key => {
          if (seen.has(key)) return;
          orderedFromStructure.push(key);
          seen.add(key);
        });
      });

      return orderedFromStructure;
    },
    collectFieldIdsFromStructure(fields = []) {
      const result = [];
      const seen = new Set();
      const containerTypes = new Set([
        "two-column",
        "three-column",
        "multi-step"
      ]);

      const visit = fieldList => {
        (fieldList || []).forEach(field => {
          if (!field || typeof field !== "object") return;
          const type = String(field.type || "").trim();
          const isContainerType = containerTypes.has(type);

          if (field.id && !isContainerType && !seen.has(field.id)) {
            result.push(field.id);
            seen.add(field.id);
          }

          if (Array.isArray(field.columns)) {
            field.columns.forEach(column => {
              if (Array.isArray(column)) {
                visit(column);
              }
            });
          }

          if (Array.isArray(field.steps)) {
            field.steps.forEach(step => {
              if (Array.isArray(step?.fields)) {
                visit(step.fields);
              }
            });
          }
        });
      };

      visit(fields);
      return result;
    },
    buildSubmissionCsv(submissions = [], fieldIds = [], formId = "") {
      const metaHeaders = ["Eingereicht am", "Status", "Formular"];
      const fieldHeaders = fieldIds.map(fieldId =>
        this.getFieldLabel(fieldId, formId)
      );
      const headers = [...metaHeaders, ...fieldHeaders];

      const rows = submissions.map(submission => {
        const formId = this.getFormId(submission.form_id);
        const values = fieldIds.map(fieldId =>
          this.formatSubmissionValue(submission?.data?.[fieldId])
        );

        return [
          this.formatDate(submission.createdAt),
          this.getStatusLabel(submission.status),
          this.getFormName(formId),
          ...values
        ];
      });

      return [headers, ...rows]
        .map(row => row.map(this.escapeCsvValue).join(";"))
        .join("\n");
    },
    escapeCsvValue(value) {
      const normalized = String(value ?? "")
        .replace(/\r?\n|\r/g, " ")
        .trim();
      const escaped = normalized.replace(/"/g, '""');
      return `"${escaped}"`;
    },
    buildSubmissionCsvFilename(formId = "") {
      const now = new Date();
      const pad = number => String(number).padStart(2, "0");
      const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
        now.getDate()
      )}_${pad(now.getHours())}-${pad(now.getMinutes())}`;
      return `form-submissions-${formId || "export"}-${stamp}.csv`;
    },
    downloadCsvFile(csvContent, filename) {
      const blob = new Blob(["\uFEFF", csvContent], {
        type: "text/csv;charset=utf-8;"
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    matchesSubmissionFilters(submission) {
      const sFormId = this.getFormId(submission.form_id);
      if (!this.canBypassSubmissionReadGroup && sFormId) {
        if (!this.userHasConfiguredReadGroup(sFormId)) return false;
      }

      if (this.filters.search) {
        const name = this.getFormName(submission.form_id).toLowerCase();
        if (!name.includes(this.filters.search.toLowerCase())) return false;
      }

      if (this.filters.formId && sFormId !== this.filters.formId) return false;
      if (this.filters.status && submission.status !== this.filters.status)
        return false;

      return true;
    },

    applySubmissionFilters(submissions = []) {
      return submissions.filter(this.matchesSubmissionFilters);
    },

    getFormId(formRef) {
      if (!formRef) return null;
      if (typeof formRef === "string") return formRef;
      return formRef._id || formRef.id || null;
    },

    async fetchSubmissions() {
      try {
        this.loading = true;
        const headers = getTokenHeader();
        const params = {
          limit: this.pagination.itemsPerPage,
          offset: (this.pagination.page - 1) * this.pagination.itemsPerPage
        };
        const response = await axios.get(
          `${env.form_builder_api_client_url}api/forms/${this.currentPrincipal}/submissions`,
          { params, headers }
        );
        if (response.data.success) {
          this.submissions = response.data.data || [];
          this.pagination.total = response.data.pagination?.total || 0;
          await this.preloadFormPropertiesForSubmissions(this.submissions);
        }
      } catch (error) {
        console.error("Fehler beim Laden der Einreichungen:", error);
        this.$root.$snackbar("Fehler beim Laden der Einreichungen", "error");
      } finally {
        this.loading = false;
      }
    },

    async fetchAllSubmissions() {
      const headers = getTokenHeader();
      const limit = 100;
      let offset = 0;
      let total = 0;
      const allSubmissions = [];

      do {
        const response = await axios.get(
          `${env.form_builder_api_client_url}api/forms/${this.currentPrincipal}/submissions`,
          {
            params: { limit, offset },
            headers
          }
        );

        if (!response.data.success) break;

        const chunk = response.data.data || [];
        total = Number(response.data.pagination?.total || 0);
        allSubmissions.push(...chunk);
        offset += chunk.length;

        if (!chunk.length) break;
      } while (offset < total);

      await this.preloadFormPropertiesForSubmissions(allSubmissions);
      return allSubmissions;
    },

    async loadFormStructure(formId) {
      if (this.formStructures[formId]) return;
      try {
        const headers = getTokenHeader();
        const response = await axios.get(
          `${env.form_builder_api_client_url}api/forms/${this.currentPrincipal}/${formId}`,
          { headers }
        );
        if (response.data.success) {
          this.$set(this.formStructures, formId, response.data.data);
        }
      } catch (error) {
        console.error("Fehler beim Laden der Formularstruktur:", error);
      }
    },

    async viewSubmission(submission) {
      const formId = this.getFormId(submission.form_id);
      if (formId) await this.loadFormStructure(formId);
      this.selectedSubmission = submission;
      await this.$nextTick();
      this.viewDialog = true;
    },

    getFieldLabel(fieldId, formRef) {
      if (!fieldId || !formRef) return fieldId;
      const formId = this.getFormId(formRef);
      if (!formId) return fieldId;
      const form = this.formStructures[formId];
      if (form && form.data?.fields) {
        const field = this.findFieldInForm(form.data.fields, fieldId);
        if (field) {
          const label = field.label?.trim();
          if (label && label.length > 0) return label;
          // Fallback: Feldtyp als lesbaren Namen anzeigen
          return this.getFieldTypeLabel(field.type);
        }
      }
      return fieldId;
    },

    getFieldTypeLabel(type) {
      const map = {
        text: "Textfeld",
        email: "E-Mail",
        phone: "Telefon",
        url: "URL",
        number: "Zahl",
        password: "Passwort",
        textarea: "Textbereich",
        select: "Dropdown",
        checkbox: "Checkbox",
        "checkbox-image": "Checkbox mit Bild",
        radio: "Radio Button",
        date: "Datum",
        file: "Dateiupload",
        image: "Bild-Upload",
        hidden: "Verstecktes Feld"
      };
      return map[type] || type;
    },

    isInfoOnlySubmission(submission) {
      const value = submission?.infoOnly;
      if (typeof value === "string") return value.toLowerCase() === "true";
      return value === true || value === 1;
    },

    findFieldInForm(fields, fieldId) {
      if (!fields || !Array.isArray(fields)) return null;
      for (const field of fields) {
        if (field.id === fieldId) return field;
        if (field.columns && Array.isArray(field.columns)) {
          for (const column of field.columns) {
            const found = this.findFieldInForm(column, fieldId);
            if (found) return found;
          }
        }
        if (field.steps && Array.isArray(field.steps)) {
          for (const step of field.steps) {
            if (step.fields) {
              const found = this.findFieldInForm(step.fields, fieldId);
              if (found) return found;
            }
          }
        }
      }
      return null;
    },

    editSubmissionStatus(submission) {
      this.selectedSubmission = submission;
      this.editingSubmissionStatus = submission.status || "submitted";
      this.editingSubmissionNotes = submission.notes || "";
      this.editStatusDialog = true;
    },

    async updateSubmissionStatus() {
      try {
        const headers = getTokenHeader();
        await axios.patch(
          `${env.form_builder_api_client_url}api/forms/${this.currentPrincipal}/submissions/${this.selectedSubmission._id}`,
          {
            status: this.editingSubmissionStatus,
            notes: this.editingSubmissionNotes
          },
          { headers }
        );
        this.editStatusDialog = false;
        await this.fetchSubmissions();
        this.$root.$snackbar("Status aktualisiert", "success");
      } catch (error) {
        console.error("Fehler beim Aktualisieren des Status:", error);
        this.$root.$snackbar("Fehler beim Aktualisieren des Status", "error");
      }
    },

    confirmDelete(submission) {
      this.openWarningDialog("delete", submission);
    },

    confirmResend(submission) {
      this.openWarningDialog("resend", submission);
    },

    async confirmBatchDelete() {
      const selectedIds = new Set(
        this.selected.map(item => item?._id).filter(Boolean)
      );
      const selectedFromFilteredList = this.filteredSubmissions.filter(item =>
        selectedIds.has(item._id)
      );
      let submissions = selectedFromFilteredList;

      if (this.allPagesSelected) {
        this.selectAllLoading = true;
        try {
          const allSubmissions = await this.fetchAllSubmissions();
          submissions = this.applySubmissionFilters(allSubmissions);
          this.allPagesSelectionCount = submissions.length;
        } catch (error) {
          console.error("Fehler beim Laden aller Einreichungen:", error);
          this.$root.$snackbar("Fehler beim Laden der Auswahl", "error");
          return;
        } finally {
          this.selectAllLoading = false;
        }
      }

      if (!submissions.length) {
        this.$root.$snackbar("Keine Einreichungen ausgewählt", "error");
        return;
      }

      this.warningSubmissions = submissions;
      this.openWarningDialog("batchDelete");
    },

    async selectAllPages() {
      if (this.selectAllLoading) return;
      this.selectAllLoading = true;
      try {
        const allSubmissions = await this.fetchAllSubmissions();
        const allFiltered = this.applySubmissionFilters(allSubmissions);
        this.selected = allFiltered;
        this.allPagesSelected = true;
        this.allPagesSelectionCount = allFiltered.length;
      } catch (error) {
        console.error("Fehler bei der Auswahl aller Einreichungen:", error);
        this.$root.$snackbar(
          "Fehler beim Auswählen aller Einreichungen",
          "error"
        );
      } finally {
        this.selectAllLoading = false;
      }
    },

    clearSelection() {
      this.selected = [];
      this.allPagesSelected = false;
      this.allPagesSelectionCount = 0;
    },

    openWarningDialog(action, submission = null) {
      this.warningAction = action;
      this.warningSubmission = submission;
      this.warningDialog = true;
    },

    closeWarningDialog() {
      this.warningDialog = false;
      this.warningAction = null;
      this.warningSubmission = null;
      this.warningSubmissions = [];
    },

    getWarningDialogMessage() {
      const formName = this.getFormName(this.warningSubmission?.form_id);
      if (this.warningAction === "delete") {
        return `Sind Sie sicher, dass Sie die Einreichung von "${formName}" löschen wollen?`;
      }
      if (this.warningAction === "resend") {
        return `Soll die Einreichung von "${formName}" wirklich erneut versendet werden?`;
      }
      if (this.warningAction === "batchDelete") {
        const count = this.warningSubmissions.length;
        return `Sind Sie sicher, dass Sie ${count} Einreichung${
          count === 1 ? "" : "en"
        } löschen wollen?`;
      }
      return "Sind Sie sicher?";
    },

    async confirmWarningAction() {
      const submission = this.warningSubmission;
      const submissions = this.warningSubmissions;
      const action = this.warningAction;
      this.closeWarningDialog();
      if (action === "delete" && submission) {
        await this.deleteSubmission(submission);
      } else if (action === "resend" && submission) {
        await this.resendSubmission(submission);
      } else if (action === "batchDelete") {
        await this.deleteSelectedSubmissions(submissions);
      }
    },

    async deleteSubmissionRequest(submissionId) {
      const headers = getTokenHeader();
      await axios.delete(
        `${env.form_builder_api_client_url}api/forms/${this.currentPrincipal}/submissions/${submissionId}`,
        { headers }
      );
    },

    async deleteSubmission(submission) {
      try {
        await this.deleteSubmissionRequest(submission._id);
        await this.fetchSubmissions();
        this.clearSelection();
        this.$root.$snackbar("Einreichung gelöscht", "success");
      } catch (error) {
        console.error("Fehler beim Löschen der Einreichung:", error);
        this.$root.$snackbar("Fehler beim Löschen der Einreichung", "error");
      }
    },

    async deleteSelectedSubmissions(submissions) {
      if (!submissions?.length) return;

      this.batchDeleteLoading = true;
      try {
        const uniqueIds = [
          ...new Set(submissions.map(item => item?._id).filter(Boolean))
        ];
        const results = await Promise.allSettled(
          uniqueIds.map(id => this.deleteSubmissionRequest(id))
        );
        const successCount = results.filter(
          result => result.status === "fulfilled"
        ).length;
        const errorCount = results.length - successCount;

        await this.fetchSubmissions();
        this.clearSelection();

        if (successCount) {
          this.$root.$snackbar(
            `${successCount} Einreichung${
              successCount === 1 ? "" : "en"
            } gelöscht`,
            "success"
          );
        }
        if (errorCount) {
          this.$root.$snackbar(
            `${errorCount} Einreichung${errorCount === 1 ? "" : "en"} konnte${
              errorCount === 1 ? "" : "n"
            } nicht gelöscht werden`,
            "error"
          );
        }
      } finally {
        this.batchDeleteLoading = false;
      }
    },

    async resendSubmission(submission) {
      if (!submission?._id || this.resendLoadingId === submission._id) return;
      try {
        this.resendLoadingId = submission._id;
        const headers = getTokenHeader();
        await axios.post(
          `${env.form_builder_api_client_url}api/forms/${this.currentPrincipal}/submissions/${submission._id}/resend`,
          {},
          { headers }
        );
        this.$root.$snackbar("Einreichung erneut versendet", "success");
      } catch (error) {
        console.error("Fehler beim erneuten Versenden der Einreichung:", error);
        this.$root.$snackbar("Fehler beim erneuten Versenden", "error");
      } finally {
        this.resendLoadingId = null;
      }
    },

    getFormName(formRef) {
      if (!formRef) return "-";
      // Wenn form_id populated ist, hat es direkt einen Namen
      if (typeof formRef === "object" && formRef.name) return formRef.name;
      const id = this.getFormId(formRef);
      if (!id) return "-";
      const form = this.formStructures[id];
      return form?.name || id;
    },

    getStatusLabel(status) {
      const map = {
        submitted: "Eingereicht",
        read: "Gelesen",
        archived: "Archiviert",
        // Legacy-Fallbacks
        reviewing: "Gelesen",
        completed: "Archiviert"
      };
      return map[status] || status || "-";
    },

    getStatusColor(status) {
      const map = {
        submitted: "#1976d2",
        read: "#ff9800",
        archived: "#4caf50",
        // Legacy-Fallbacks
        reviewing: "#ff9800",
        completed: "#4caf50"
      };
      return map[status] || "#757575";
    },

    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleDateString("de-DE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    isLikelyUrl(value) {
      if (!value || typeof value !== "string") return false;
      return /^https?:\/\//i.test(value.trim());
    },

    getFileNameFromUrl(url) {
      if (!url) return "Datei";
      try {
        const pathname = new URL(url).pathname || "";
        const segments = pathname.split("/").filter(Boolean);
        return segments[segments.length - 1] || "Datei";
      } catch (e) {
        return "Datei";
      }
    },

    extractSubmissionFileMeta(value) {
      if (!value) return null;

      if (typeof value === "string") {
        if (!this.isLikelyUrl(value)) return null;
        const url = value.trim();
        return {
          url,
          label: this.getFileNameFromUrl(url),
          mimeType: ""
        };
      }

      if (typeof value !== "object") return null;

      const url = String(value.url || value.href || "").trim();
      if (!this.isLikelyUrl(url)) return null;

      return {
        url,
        label:
          String(value.title || "").trim() ||
          String(value.name || "").trim() ||
          this.getFileNameFromUrl(url),
        mimeType: String(value.mimeType || value.type || "").trim()
      };
    },

    getSubmissionFileLink(value) {
      return this.extractSubmissionFileMeta(value)?.url || "";
    },

    getSubmissionFileLabel(value) {
      const meta = this.extractSubmissionFileMeta(value);
      if (!meta) return "";
      return meta.label || "Datei";
    },

    isSubmissionImage(value) {
      const meta = this.extractSubmissionFileMeta(value);
      if (!meta?.url) return false;

      if (meta.mimeType.startsWith("image/")) return true;
      return /\.(png|jpe?g|gif|webp|bmp|svg|avif)(\?.*)?$/i.test(meta.url);
    },

    formatSubmissionValue(value) {
      if (value === null || value === undefined || value === "") return "-";

      if (typeof value === "string" || typeof value === "number") {
        return String(value);
      }

      if (Array.isArray(value)) {
        if (value.length === 0) return "-";
        return (
          value
            .map(item => this.formatSubmissionValue(item))
            .filter(item => item && item !== "-")
            .join(", ") || "-"
        );
      }

      if (typeof value === "object") {
        const fileMeta = this.extractSubmissionFileMeta(value);
        if (fileMeta) {
          const label = fileMeta.label || "Datei";
          return `${label} (${fileMeta.url})`;
        }

        // Legacy-Format: { name: "..." } / { value: "..." }
        if (value.name) return String(value.name);
        if (value.value) return String(value.value);

        // Neues Format: { "optionsname": "optionswert" }
        const entries = Object.entries(value)
          .filter(([, v]) => v !== null && v !== undefined && v !== "")
          .map(([k, v]) => `${k}: ${v}`);

        if (entries.length > 0) return entries.join(", ");
      }

      return "-";
    },

    async onTableOptionsUpdated(nextOptions = {}) {
      const requestedPage = Number(nextOptions.page);
      const nextPage =
        Number.isFinite(requestedPage) && requestedPage > 0
          ? requestedPage
          : this.pagination.page;
      const requestedItemsPerPage = Number(nextOptions.itemsPerPage);
      const nextItemsPerPage =
        Number.isFinite(requestedItemsPerPage) && requestedItemsPerPage > 0
          ? requestedItemsPerPage
          : this.pagination.itemsPerPage;

      const hasChanged =
        nextPage !== this.pagination.page ||
        nextItemsPerPage !== this.pagination.itemsPerPage;

      if (!hasChanged) return;

      this.clearSelection();
      this.pagination.page = nextPage;
      this.pagination.itemsPerPage = nextItemsPerPage;
      await this.fetchSubmissions();
    },

    async preloadFormPropertiesForSubmissions(submissions) {
      const uniqueFormIds = [
        ...new Set((submissions || []).map(s => this.getFormId(s.form_id)))
      ].filter(Boolean);
      await Promise.all(
        uniqueFormIds.map(formId => this.loadFormProperties(formId))
      );
    },

    async loadFormProperties(formId) {
      if (!formId || this.formPropertyCache[formId]) return;
      try {
        const headers = getTokenHeader();
        const response = await axios.get(
          `${env.form_builder_api_client_url}api/form-properties/${this.currentPrincipal}/${formId}`,
          { headers }
        );
        this.$set(this.formPropertyCache, formId, response?.data?.data || {});
      } catch (error) {
        // Wenn keine Properties vorhanden sind, wird keine Leserechtsgruppe erzwungen.
        this.$set(this.formPropertyCache, formId, {});
      }
    },

    normalizeGroupIds(groups) {
      if (!Array.isArray(groups)) return [];
      return groups
        .map(group => {
          if (typeof group === "string") return group;
          return group?.id || group?._id || null;
        })
        .filter(Boolean)
        .map(String);
    },

    userHasConfiguredReadGroup(formId) {
      const property = this.formPropertyCache[formId] || {};
      const configuredGroupId = property.submissionReaderGroupId;
      if (!configuredGroupId) return true;

      const groups = this.$store.state.groups || [];
      const matchedGroup = groups.find(group => {
        return (
          String(group?.id || "") === String(configuredGroupId) ||
          String(group?._id || "") === String(configuredGroupId)
        );
      });

      const allowedIds = [String(configuredGroupId)];
      if (matchedGroup?.id) allowedIds.push(String(matchedGroup.id));
      if (matchedGroup?._id) allowedIds.push(String(matchedGroup._id));

      const currentUserGroupIds = this.normalizeGroupIds(
        this.currentUser?.groups
      );
      return allowedIds.some(id => currentUserGroupIds.includes(id));
    }
  },

  computed: {
    currentPrincipal() {
      return this.$store.state.selectedPrincipal.id;
    },
    tableOptions() {
      return {
        page: this.pagination.page,
        itemsPerPage: this.pagination.itemsPerPage
      };
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    isSuperAdmin() {
      return this.currentUser?.scopes?.includes("role:admin");
    },
    isPrincipalAdmin() {
      return (
        this.currentUser?.principal_id === this.currentPrincipal &&
        !!this.currentUser?.additional_data?.eberl_admin
      );
    },
    canBypassSubmissionReadGroup() {
      return this.isSuperAdmin || this.isPrincipalAdmin;
    },

    formFilterItems() {
      const uniqueFormIds = [
        ...new Set(this.submissions.map(s => this.getFormId(s.form_id)))
      ].filter(Boolean);
      return uniqueFormIds.map(id => ({ _id: id, name: this.getFormName(id) }));
    },

    filteredSubmissions() {
      return this.applySubmissionFilters(this.submissions || []);
    },
    visibleSelectedCount() {
      const selectedIds = new Set(
        this.selected.map(item => item?._id).filter(Boolean)
      );
      return this.filteredSubmissions.filter(item => selectedIds.has(item._id))
        .length;
    },
    hasSelectionContext() {
      return this.allPagesSelected || this.visibleSelectedCount > 0;
    },
    selectionLabel() {
      if (this.allPagesSelected) {
        return `Alle ${this.allPagesSelectionCount} Einreichungen ausgewählt`;
      }
      return `${this.visibleSelectedCount} ausgewählt`;
    }
  },

  watch: {
    "filters.search"() {
      this.clearSelection();
    },
    "filters.formId"() {
      this.clearSelection();
    },
    "filters.status"() {
      this.clearSelection();
    },
    currentPrincipal() {
      this.clearSelection();
      this.pagination.page = 1;
      this.formStructures = {};
      this.fetchSubmissions();
    }
  }
};
